import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useSearchParams, Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Toaster } from "@/components/ui/sonner";
import { WelcomeDialog } from "@/components/WelcomeDialog";
import { MeasurementsProvider } from "@/context/MeasurementsContext";
import { VehicleProvider, useVehicle } from "@/context/VehicleContext";
import { UnitsProvider } from "@/context/UnitsContext";
import { LicenseProvider, useLicense } from "@/context/LicenseContext";
import { LicenseGate } from "@/components/LicenseGate";
import { Splash } from "@/components/Splash";
import Calculator from "@/pages/Calculator";
import QuickAlign from "@/pages/QuickAlign";
import SpecVault from "@/pages/SpecVault";
import SessionLog from "@/pages/SessionLog";
import Guide from "@/pages/Guide";
import VisualScan from "@/pages/VisualScan";
import Instructions from "@/pages/Instructions";
import TiltGauge from "@/pages/TiltGauge";
import Garage from "@/pages/Garage";
import ARAlign from "@/pages/ARAlign";
import TireWear from "@/pages/TireWear";
import CornerBalance from "@/pages/CornerBalance";
import ReportBuilder from "@/pages/ReportBuilder";
import ReportView from "@/pages/ReportView";
import Legal from "@/pages/Legal";
import Contact from "@/pages/Contact";
import AdKit from "@/pages/AdKit";
import SensorCheck from "@/pages/SensorCheck";
import Landing from "@/pages/Landing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import { NoVehicle } from "@/components/NoVehicle";

function FooterVehicle() {
  const { vehicleLabel } = useVehicle();
  return <>{vehicleLabel}</>;
}

function RequireVehicle({ children, tkey }) {
  const { hasVehicle } = useVehicle();
  return hasVehicle ? children : <NoVehicle tkey={tkey} />;
}

function LicenseUrlWatcher() {
  const [params, setParams] = useSearchParams();
  const { resolveUrlLicense } = useLicense();
  const lic = params.get("license");
  useEffect(() => {
    if (!lic) return;
    resolveUrlLicense(lic).finally(() => {
      const next = new URLSearchParams(params);
      next.delete("license");
      setParams(next, { replace: true });
    });
  }, [lic]);
  return null;
}

function AppShell() {
  const { unlocked, checking } = useLicense();
  if (checking) {
    return <Splash label="Starting up" />;
  }
  if (!unlocked) return <LicenseGate />;
  return (
    <>
      <WelcomeDialog />
      <Nav />
      <main>
        <Outlet />
      </main>
      <footer className="border-t border-steel mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-slate-600 tracking-widest uppercase">
            Thortek Performance · <FooterVehicle />
          </span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-slate-500 tracking-widest uppercase">
            <Link to="/legal/disclaimer" data-testid="app-footer-disclaimer-link" className="hover:text-cyan transition-colors">Disclaimer</Link>
            <Link to="/legal/terms" data-testid="app-footer-terms-link" className="hover:text-cyan transition-colors">Terms</Link>
            <Link to="/legal/privacy" data-testid="app-footer-privacy-link" className="hover:text-cyan transition-colors">Privacy</Link>
            <Link to="/contact" data-testid="app-footer-contact-link" className="hover:text-cyan transition-colors">Contact</Link>
          </div>
          <span className="font-mono text-[11px] text-slate-600 tracking-widest">
            Reference values — verify against manufacturer service info
          </span>
        </div>
      </footer>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-ink text-white">
      <VehicleProvider>
      <MeasurementsProvider>
      <UnitsProvider>
      <LicenseProvider>
        <BrowserRouter>
          <LicenseUrlWatcher />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/report/:id" element={<ReportView />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/ad-kit" element={<AdKit />} />
            <Route path="/sensor-check" element={<SensorCheck />} />
            <Route element={<AppShell />}>
              <Route path="/app" element={<RequireVehicle tkey="novehicle.pickStart"><QuickAlign /></RequireVehicle>} />
              <Route path="/calculator" element={<RequireVehicle tkey="novehicle.pickCalc"><Calculator /></RequireVehicle>} />
              <Route path="/scan" element={<RequireVehicle tkey="novehicle.pickScan"><VisualScan /></RequireVehicle>} />
              <Route path="/gauge" element={<RequireVehicle tkey="novehicle.pickGauge"><TiltGauge /></RequireVehicle>} />
              <Route path="/ar" element={<RequireVehicle tkey="novehicle.pickStart"><ARAlign /></RequireVehicle>} />
              <Route path="/tire" element={<RequireVehicle tkey="novehicle.pickStart"><TireWear /></RequireVehicle>} />
              <Route path="/balance" element={<CornerBalance />} />
              <Route path="/report" element={<RequireVehicle tkey="novehicle.pickStart"><ReportBuilder /></RequireVehicle>} />
              <Route path="/garage" element={<Garage />} />
              <Route path="/specs" element={<SpecVault />} />
              <Route path="/log" element={<SessionLog />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/help" element={<Instructions />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </LicenseProvider>
      </UnitsProvider>
      </MeasurementsProvider>
      </VehicleProvider>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
